import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { Store } from 'redux';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
    ConfigView,
    flattenMetrics,
    HierarchyUserInfoItem,
    Metric,
    QuickSwitchItem,
    RoleHierarchyItem,
} from '../insight/config.insight.model';
import {
    visualRetrieveData,
    visualRetrieveDataCancel,
} from '../insight/insight.action';
import { VisualState } from '../insight/insight.state';
import { OptionalColumn } from '../insight/optional-column.insight.model';
import {
    PerformanceDataViewType,
    PerformanceDataViewTypeArgsMap,
} from '../insight/performance-data/metric.pd.model';
import { VisualItemInsightComponent } from '../insight/visual-item.insight.component';
import {
    LayoutQuery,
    VisualItemClickEvent,
} from '../insight/visual-item.insight.model';
import { StateToProps, storeObserver } from '../store';
import { STORE } from '../store.metadata';
import { PeriodType } from '../widget/date-range.widget.model';

import { SuperGroupExtra } from './super-group.hi.model';

@Component({
    selector: '[cmpsHumanInsightVisual]',
    templateUrl: './visual.hi.component.html',
})
export class VisualHumanInsightComponent
    implements OnChanges, OnInit, OnDestroy, StateToProps<VisualState>
{
    @ViewChildren(VisualItemInsightComponent)
    items: QueryList<VisualItemInsightComponent>;

    @Input() view: ConfigView;
    @Input() metrics: Metric[];
    @Input() periodType: PeriodType;
    @Input() eventDate: Date;
    @Input() superGroup: QuickSwitchItem<SuperGroupExtra>;
    @Input() configUserInfo: HierarchyUserInfoItem;
    @Input() roleHierarchy: RoleHierarchyItem[];
    @Input() optionalColumns: OptionalColumn[];
    @Output() itemClick: EventEmitter<VisualItemClickEvent>;
    private _storeSub: Subscription;
    state: VisualState;
    layoutQuery: LayoutQuery;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        @Inject(STORE) private readonly _store: Store,
    ) {
        this.itemClick = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.state) return;
        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('view') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('periodType') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('eventDate') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('superGroup')
        ) {
            this.propsToDispatch();
        }
    }

    ngOnInit(): void {
        this._storeSub = storeObserver<any, VisualState>(
            this._store,
            (state) => state.insight.visual,
        )
            .pipe(
                tap((state) =>
                    this._ngZone.run(() => {
                        const prevState = this.state;
                        this.state = state;
                        this.stateToProps(prevState);
                    }),
                ),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._store.dispatch(visualRetrieveDataCancel());
        if (this._storeSub) {
            this._storeSub.unsubscribe();
        }
        if (this.itemClick) {
            this.itemClick.complete();
        }
    }

    stateToProps(prevState?: VisualState): void {
        if (!prevState) {
            this.propsToDispatch();
        }
    }

    propsToDispatch(): void {
        if (this.layoutQuery) {
            this.layoutQuery.unbind();
        }
        this.layoutQuery = new LayoutQuery();
        this.layoutQuery.bind(this.view, () => {
            this.items.forEach((e) => e._layoutRows());
            this._changeDetectorRef.detectChanges();
        });
        this.layoutQuery.style();

        this._store.dispatch(visualRetrieveDataCancel());

        // const vt = PerformanceDataViewType.USER; //TODO revise use user_super_group?
        const vt = !this.superGroup.extra.teamSuperGroup
            ? PerformanceDataViewType.USER_SUPER_GROUP
            : PerformanceDataViewType.TEAM_SUPER_GROUP; // TODO revise use user_super_group?
        const ps = PerformanceDataViewTypeArgsMap[vt];
        const rh = this.roleHierarchy.find(
            (e) =>
                e.startDate &&
                e.endDate &&
                this.eventDate.getTime() >= e.startDate.getTime() &&
                this.eventDate.getTime() <= e.endDate.getTime() &&
                e.parentRoleId === this.configUserInfo.roleId,
        );

        this._store.dispatch(
            visualRetrieveData(
                {
                    identifier: this.superGroup.name,
                    viewType: vt,
                    displayNameCriteriaIdType: ps.primary,
                    outputCriteriaIdType: ps.primary,
                    outputCriteriaIdTypes: [ps.primary],
                    inputCriteriaIdTypes: null,
                    periodType: this.periodType,
                    date: this.eventDate,
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    roleId: !this.superGroup.extra.teamSuperGroup
                        ? this.configUserInfo.roleId
                        : // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                          rh && rh.roleId,
                    superGroupIdList: [
                        parseInt(this.superGroup.initialCriteria),
                    ], // TODO revise use user_super_group?
                    managerIdList: this.superGroup.extra.teamSuperGroup
                        ? [this.configUserInfo.userId]
                        : null,
                    userIdList: !this.superGroup.extra.teamSuperGroup
                        ? [this.configUserInfo.userId]
                        : null,
                    metricIdList: flattenMetrics(this.view.pillars),
                },
                [
                    {
                        identifier: this.superGroup.name,
                        performanceDataViewType: vt,
                        displayNameCriteriaIdType: ps.primary,
                        outputCriteriaType: ps.primary,
                        dataId: this.configUserInfo.userId,
                        outputCriteriaTypes: [ps.primary],
                        outputCriteriaTypeArgs: {
                            [ps.primary]: this.configUserInfo.userId,
                        },
                    },
                ],
            ),
        );
    }

    onItemClick($event: VisualItemClickEvent): void {
        this.itemClick.emit($event);
    }
}
